import { Country } from '@afterpay/types'
import { datadogRum } from '@datadog/browser-rum'
import React, { FunctionComponent, ReactElement, ReactNode, useEffect, useState } from 'react'
import { DATADOG_COOKIE, DATADOG_SITE } from './constants'
import { beforeRumSend, hasValidDatadogKeys } from './utils'
import { isNullOrUndefined, isUndefinedOrEmpty } from '@afterpay/utils'
import { parseCookies, destroyCookie } from 'nookies'

declare global {
  interface Window {
    DD_RUM?: any
  }
}

interface ApiValues {
  [key: string]: string
}
interface AppValues {
  [key: string]: string
}

// Ref: https://docs.datadoghq.com/real_user_monitoring/session_replay/privacy_options/
export enum DDPrivacyLevel {
  Mask = 'mask', // Mask all HTML text, user input, images, and links
  Hidden = 'hidden', // Advanced privacy setting that completely hides specific elements instead of obscuring the text
  Allow = 'allow', // Records everything unmasked
}

export interface DatadogMaskedElementProps {
  privacyLevel?: DDPrivacyLevel
  children: ReactNode
}

export interface DataDogUserProps {
  uuid: string | undefined
  merchantCountryCode: Country | undefined
  consumerCountryCode: string | undefined
}

interface Props {
  children?: ReactNode
  userProperties?: DataDogUserProps
  appValues: AppValues
  allowedTracingUrls?: string[]
  useCountry: () => any
}

const init = (app: AppValues, allowedTracingUrls?: string[]): void => {
  if (typeof window !== 'undefined') {
    datadogRum.init({
      beforeSend: beforeRumSend,
      applicationId: app.DATADOG_APP_ID,
      clientToken: app.DATADOG_CLIENT_TOKEN,
      site: DATADOG_SITE,
      service: app.DATADOG_SERVICE,
      version: app.APP_VERSION,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      // See https://docs.datadoghq.com/real_user_monitoring/connect_rum_and_traces/?tab=browserrum
      allowedTracingUrls,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
      env: app.APP_ENVIRONMENT,
      enableExperimentalFeatures: ['feature_flags'],
      useCrossSiteSessionCookie: true,
    })
  }
}

const setUserUUID = (uuid: string): void => {
  datadogRum.setUser({
    id: uuid,
  })
  datadogRum.setUserProperty('uuid', uuid)
}

const setUserVars = (userProperties: { [key: string]: string }): void => {
  Object.entries(userProperties).forEach(([key, value]) => {
    if (!isNullOrUndefined(value)) {
      datadogRum.setUserProperty(key, value)
    }
  })
}

export const setDatadogMetadata = (attributes: { [index: string]: any }): void => {
  Object.entries(attributes).forEach(([key, value]) => {
    datadogRum.setGlobalContextProperty(key, value)
  })
}

const stopExistingDatadogSession = (): void => {
  const cookies = parseCookies()

  if (!isUndefinedOrEmpty(cookies[DATADOG_COOKIE])) {
    stopDatadogSession()
  }
}

export const stopDatadogSession = (): void => {
  datadogRum.stopSessionReplayRecording()
  datadogRum.stopSession()
  destroyCookie(null, DATADOG_COOKIE)
}

const isLoaded = (): boolean => typeof window !== 'undefined' && typeof window.DD_RUM !== 'undefined'

export const DatadogMaskedElement: FunctionComponent<DatadogMaskedElementProps> = ({ privacyLevel = DDPrivacyLevel.Mask, children }) => {
  return (
    <div data-dd-privacy={privacyLevel} data-testid='datadog-masked-element'>
      {children}
    </div>
  )
}

export const DataDog = ({ children, userProperties, appValues, allowedTracingUrls, useCountry }: Props): ReactElement => {
  const { uuid, ...rest } = userProperties ?? {}
  const { DATADOG_APP_ID, DATADOG_CLIENT_TOKEN, DATADOG_SERVICE } = appValues
  const isDDInitialised = typeof datadogRum.getInitConfiguration() !== 'undefined' && typeof datadogRum.getInternalContext() !== 'undefined'

  const [hasInitialised, setHasInitialised] = useState(isDDInitialised)
  const [identified, setIdentified] = useState(false)

  const country = useCountry()
  const hasDatadogKeys = hasValidDatadogKeys(DATADOG_APP_ID, DATADOG_CLIENT_TOKEN, DATADOG_SERVICE)

  useEffect(() => {
    if (isLoaded() && hasDatadogKeys && !hasInitialised) {
      stopExistingDatadogSession()
      init(appValues, allowedTracingUrls)
      datadogRum.startSessionReplayRecording()
      setHasInitialised(true)
    }
  }, [hasDatadogKeys, country, hasInitialised, appValues])

  useEffect(() => {
    if (isLoaded() && (uuid ?? '').length > 0 && !identified) {
      setIdentified(true)
      uuid && setUserUUID(uuid)
    }
  }, [uuid, identified])

  useEffect(() => {
    if (identified && userProperties !== undefined) {
      setUserVars({ ...rest })
    }
  }, [rest, identified, userProperties])

  return <>{children}</>
}
